import { useTranslation } from 'react-i18next';

/**
 * PeriodPills Component
 *
 * A pill/tab selector for filtering dashboard data by time period.
 *
 * Features:
 * - Three period options: Today, This Week, This Month
 * - Active state with filled background
 * - Inactive state with outline/ghost styling
 * - Compact horizontal layout
 * - Full keyboard accessibility
 * - Responsive design (wraps on small screens if needed)
 *
 * Design:
 * - Pill-shaped buttons with rounded-full
 * - Active: Primary color filled
 * - Inactive: Outline with hover effect
 *
 * @example
 * ```tsx
 * const [period, setPeriod] = useState<'today' | 'week' | 'month'>('week');
 * <PeriodPills value={period} onChange={setPeriod} />
 * ```
 */

type Period = 'today' | 'week' | 'month';

export interface PeriodPillsProps {
  /**
   * Currently selected period
   */
  value: Period;

  /**
   * Callback when period changes
   */
  onChange: (period: Period) => void;

  /**
   * Optional count to display inside the active pill
   */
  count?: number;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const PeriodPills = ({ value, onChange, count, className = '' }: PeriodPillsProps) => {
  const { t } = useTranslation('common');

  const periods: { value: Period; labelKey: string }[] = [
    { value: 'today', labelKey: 'period_today' },
    { value: 'week', labelKey: 'period_this_week' },
    { value: 'month', labelKey: 'period_this_month' },
  ];

  return (
    <div
      className={`inline-flex items-center justify-center lg:justify-start gap-2 w-full lg:w-auto ${className}`}
      role="group"
      aria-label={t('period_filter', { defaultValue: 'Period filter' })}
    >
      {periods.map((period) => {
        const isActive = value === period.value;

        return (
          <button
            key={period.value}
            type="button"
            onClick={() => onChange(period.value)}
            aria-pressed={isActive}
            className={`
              px-2.5 h-7 inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-medium
              transition-all duration-200
              focus:outline-none
              ${
                isActive
                  ? 'bg-gray-900 text-white border border-gray-900'
                  : 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300'
              }
            `}
          >
            {t(period.labelKey, {
              defaultValue:
                period.value === 'today'
                  ? 'Today'
                  : period.value === 'week'
                    ? 'This Week'
                    : 'This Month',
            })}
            {isActive && count !== undefined && count > 0 && (
              <span
                className={`inline-grid shrink-0 place-items-center rounded-full bg-white/20 font-semibold leading-none tabular-nums ${
                  count >= 100
                    ? 'h-[19px] min-w-[19px] px-1 text-[9px]'
                    : count >= 10
                      ? 'h-[19px] w-[19px] text-[10px]'
                      : 'h-[17px] w-[17px] text-[10px]'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
